import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

export interface IPurchaseItemImportDialogProps {
    open: boolean;
    onClose: () => void;
    onImport: (file: File) => void;
    loading?: boolean;
}

export default function PurchaseItemImportDialog({ open, onClose, onImport, loading = false }: IPurchaseItemImportDialogProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (isValidFile(file)) {
                setSelectedFile(file);
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (isValidFile(file)) {
                setSelectedFile(file);
            }
        }
    };

    const isValidFile = (file: File): boolean => {
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        return validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    };

    const handleImport = () => {
        if (selectedFile) {
            onImport(selectedFile);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Importar Itens de Compra
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    sx={{
                        border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                        borderRadius: 2,
                        padding: 4,
                        textAlign: 'center',
                        backgroundColor: dragActive ? '#f0f7ff' : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#999'
                        }
                    }}
                    onClick={() => document.getElementById('file-input')?.click()}
                >
                    <input
                        id="file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />

                    {!selectedFile ? (
                        <>
                            <CloudUploadIcon sx={{ fontSize: 60, color: '#999', mb: 2 }} />
                            <Typography variant="body1" gutterBottom>
                                Arraste o arquivo aqui ou clique para selecionar
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Formatos aceitos: .xlsx, .xls
                            </Typography>
                        </>
                    ) : (
                        <>
                            <InsertDriveFileIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                            <Typography variant="body1" gutterBottom>
                                {selectedFile.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {(selectedFile.size / 1024).toFixed(2)} KB
                            </Typography>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={!selectedFile || loading}
                    startIcon={<CloudUploadIcon />}
                >
                    {loading ? 'Importando...' : 'Importar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
